import { App } from "@app/interfaces"

/**
 * Проверяет, является ли строка модульной ролью (формат: module:access)
 */
export function isModuleRole(role: string): role is App.ModuleRole {
  const pattern = /^[a-z_]+:[rw]$/
  return pattern.test(role)
}

/**
 * Извлекает модуль из модульной роли
 * @param moduleRole - роль в формате "module:access"
 * @returns модуль или undefined если роль некорректна
 */
export function getModuleFromRole(
  moduleRole: string,
): App.ModuleName | undefined {
  if (!isModuleRole(moduleRole)) {
    return undefined
  }

  const [moduleName] = moduleRole.split(":") as [string, App.AccessType]
  return moduleName as App.ModuleName
}

/**
 * Извлекает тип доступа из модульной роли
 * @param moduleRole - роль в формате "module:access"
 * @returns тип доступа или undefined если роль некорректна
 */
export function getAccessFromRole(
  moduleRole: string,
): App.AccessType | undefined {
  if (!isModuleRole(moduleRole)) {
    return undefined
  }

  const [, access] = moduleRole.split(":") as [string, App.AccessType]
  return access
}

/**
 * Получает список модулей из массива ролей пользователя
 * @param roles - массив всех ролей пользователя
 * @returns массив уникальных модулей
 */
export function getModulesFromRoles(roles: string[]): App.ModuleName[] {
  const modules = new Set<App.ModuleName>()

  for (const role of roles) {
    const module = getModuleFromRole(role)
    if (module) {
      modules.add(module)
    }
  }

  return Array.from(modules)
}

/**
 * Проверяет, есть ли у пользователя доступ к модулю (любой: read или write)
 * @param userRoles - массив ролей пользователя
 * @param module - модуль для проверки
 * @returns true если есть доступ к модулю (read или write)
 */
export function hasModuleAccess(
  userRoles: string[],
  module: App.ModuleName,
): boolean {
  const readRole = `${module}:${App.AccessType.READ}` as App.ModuleRole
  const writeRole = `${module}:${App.AccessType.WRITE}` as App.ModuleRole

  return userRoles.includes(readRole) || userRoles.includes(writeRole)
}

/**
 * Проверяет, есть ли у пользователя доступ на запись к модулю
 * @param userRoles - массив ролей пользователя
 * @param module - модуль для проверки
 * @returns true если есть доступ на запись
 */
export function hasModuleWriteAccess(
  userRoles: string[],
  module: App.ModuleName,
): boolean {
  const writeRole = `${module}:${App.AccessType.WRITE}` as App.ModuleRole
  return userRoles.includes(writeRole)
}

/**
 * Проверяет, есть ли у пользователя доступ на чтение к модулю
 * @param userRoles - массив ролей пользователя
 * @param module - модуль для проверки
 * @returns true если есть доступ на чтение
 */
export function hasModuleReadAccess(
  userRoles: string[],
  module: App.ModuleName,
): boolean {
  const readRole = `${module}:${App.AccessType.READ}` as App.ModuleRole
  return userRoles.includes(readRole)
}

/**
 * Фильтрует роли, оставляя только модульные роли
 * @param roles - массив всех ролей
 * @returns массив только модульных ролей
 */
export function filterModuleRoles(roles: string[]): App.ModuleRole[] {
  return roles.filter(isModuleRole)
}
